import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import type { APIResponse } from "@/lib/types";

export type ApproachingStatutesReportRow = {
  incidentTypeId: string | null;
  incidentTypeLabel: string | null;
  claimTypes: Array<{ id: string; label: string }>;
  daysRemaining: number;
  matterId: string;
  matterStatusLabel: string;
  matterTitle: string;
  statuteOfLimitations: string;
  statuteOfLimitationsConfirmed: boolean | null;
};

export type ApproachingStatutesReportResponse = {
  metrics: {
    confirmed: number;
    needsConfirmation: number;
    total: number;
  };
  rows: ApproachingStatutesReportRow[];
};

export type RetrieveApproachingStatutesReportInput = {
  filters: {
    incidentTypeIds?: string[];
    claimTypeIds?: string[];
    matterStatusLabels: string[];
    windowDays: 30 | 60 | 90 | 120;
  };
};

type ApproachingStatutesApiResponse = Omit<
  APIResponse<ApproachingStatutesReportResponse>,
  "pagination"
> & {
  pagination?: { hasMore?: boolean };
};

const PAGE_SIZE = 250;
const MAX_PAGES = 1000;

export const retrieveApproachingStatutesReport = async (
  data: RetrieveApproachingStatutesReportInput,
) => {
  const rows: ApproachingStatutesReportRow[] = [];
  let pageNumber = 1;
  let firstPage: ApproachingStatutesReportResponse | undefined;
  let meta: APIResponse<ApproachingStatutesReportResponse>["meta"] | undefined;
  let hasMore = false;

  do {
    const response =
      await authenticatedFastApiAxiosInstance.post<ApproachingStatutesApiResponse>(
        "reports/v2/approaching-statutes/query",
        {
          ...data,
          pageNumber,
          pageSize: PAGE_SIZE,
        },
      );

    firstPage ??= response.data.data;
    meta = response.data.meta;
    rows.push(...response.data.data.rows);
    hasMore = response.data.pagination?.hasMore === true;
    pageNumber += 1;
  } while (hasMore && pageNumber <= MAX_PAGES);

  if (hasMore) {
    throw new Error(
      `The approaching statutes report exceeded the ${MAX_PAGES}-page retrieval limit.`,
    );
  }

  return {
    data: {
      ...firstPage,
      rows,
    },
    meta,
  } satisfies APIResponse<ApproachingStatutesReportResponse>;
};
